<template>
  <div class="sidebar" :class="{ collapsed: collapsed }">
    <div class="sidebar-header">
      <img src="@/assets/img/logo.png" alt="Logo" class="logo">
      <span class="company-name" v-if="!collapsed">LubConsulta</span>
    </div>
    
    <nav class="sidebar-nav">
      <div v-for="menu in menus" :key="menu.id" class="menu-section">
        <div 
          class="menu-item" 
          @click="toggleMenu(menu)"
          :data-tooltip="collapsed ? menu.label : ''"
        >
          <span class="menu-icon">{{ menu.icon }}</span>
          <span class="menu-label" v-if="!collapsed">{{ menu.label }}</span>
          <span class="menu-arrow" v-if="!collapsed" :class="{ rotated: menu.isOpen }">›</span>
        </div>
        
        <!-- Submenu flutuante quando sidebar está recolhido -->
        <div 
          v-if="collapsed && menu.isOpen && menu.submenus" 
          class="floating-submenu"
          @mouseleave="closeFloatingSubmenu"
        >
          <div 
            v-for="submenu in menu.submenus" 
            :key="submenu.id" 
            class="submenu-item"
            @click="navigateTo(submenu)"
          >
            <span class="submenu-icon">{{ submenu.icon }}</span>
            <span class="submenu-label">{{ submenu.label }}</span>
          </div>
        </div>
        
        <!-- Submenu normal quando sidebar está expandido -->
        <div v-if="!collapsed && menu.isOpen && menu.submenus" class="submenu">
          <div 
            v-for="submenu in menu.submenus" 
            :key="submenu.id" 
            class="submenu-item"
            @click="navigateTo(submenu)"
          >
            <span class="submenu-icon">{{ submenu.icon }}</span>
            <span class="submenu-label">{{ submenu.label }}</span>
          </div>
        </div>
      </div>
      
      <!-- Menu específico para PageDefault -->
      <div class="menu-section">
        <div 
          class="menu-item" 
          @click="navigateToPageDefault"
          :data-tooltip="collapsed ? 'Gestão Empresas' : ''"
        >
          <span class="menu-icon">📋</span>
          <span class="menu-label" v-if="!collapsed">Gestão </span>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
export default {
  name: 'AppSidebar',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menus: [
        {
          id: 1,
          label: 'Dashboard',
          icon: '📊',
          isOpen: false,
          submenus: [
            { id: 11, label: 'Visão Geral', icon: '👁️', route: '/dashboard' },
            { id: 12, label: 'Analytics', icon: '📈', route: '/analytics' }
          ]
        },
        {
          id: 2,
          label: 'Empresas',
          icon: '🏢',
          isOpen: false,
          submenus: [
            { id: 21, label: 'Clientes', icon: '📋', route: '/pagedefault' },
            { id: 22, label: 'Cadastrar', icon: '➕', route: '/empresas/cadastrar' }
          ]
        },
        {
          id: 3,
          label: 'Financeiro',
          icon: '💰',
          isOpen: false,
          submenus: [
            { id: 31, label: 'Faturas', icon: '🧾', route: '/financeiro/faturas' },
            { id: 32, label: 'Relatórios', icon: '📊', route: '/financeiro/relatorios' }
          ]
        },
        {
          id: 4,
          label: 'Configurações',
          icon: '⚙️',
          isOpen: false,
          submenus: [
            { id: 41, label: 'Usuários', icon: '👥', route: '/config/usuarios' },
            { id: 42, label: 'Permissões', icon: '🔒', route: '/config/permissoes' }
          ]
        }
      ],
      activeFloatingMenu: null
    }
  },
  methods: {
    toggleMenu(clickedMenu) {
      if (this.collapsed) {
        // Quando sidebar está recolhido, abre o submenu flutuante
        this.closeAllOtherMenus(clickedMenu.id);
        clickedMenu.isOpen = true;
        this.activeFloatingMenu = clickedMenu.id;
      } else {
        // Quando sidebar está expandido, fecha outros menus e abre/clica o atual
        this.closeAllOtherMenus(clickedMenu.id);
        clickedMenu.isOpen = !clickedMenu.isOpen;
      }
    },
    
    closeAllOtherMenus(currentMenuId) {
      // Fecha todos os menus exceto o que foi clicado
      this.menus.forEach(menu => {
        if (menu.id !== currentMenuId) {
          menu.isOpen = false;
        }
      });
      
      // Se estiver recolhido, atualiza o menu flutuante ativo
      if (this.collapsed) {
        this.activeFloatingMenu = currentMenuId;
      }
    },
    
    closeFloatingSubmenu() {
      // Fecha o submenu flutuante após um pequeno delay
      setTimeout(() => {
        if (this.activeFloatingMenu) {
          const menu = this.menus.find(m => m.id === this.activeFloatingMenu);
          if (menu) {
            menu.isOpen = false;
          }
          this.activeFloatingMenu = null;
        }
      }, 300);
    },
    
    closeAllFloatingMenus() {
      this.menus.forEach(menu => {
        menu.isOpen = false;
      });
      this.activeFloatingMenu = null;
    },
    
    navigateTo(submenu) {
      if (submenu.route) {
        this.$router.push(submenu.route);
        // Fecha todos os menus após navegação
        this.closeAllFloatingMenus();
      }
    },
    
    navigateToPageDefault() {
      this.$router.push('/pagedefault');
      this.closeAllFloatingMenus();
    }
  },
  
  watch: {
    collapsed(newVal) {
      if (newVal) {
        // Quando sidebar recolhe, fecha todos os menus
        this.closeAllFloatingMenus();
      }
    }
  }
}
</script>

<style scoped>
.sidebar {
  width: 280px;
  background: linear-gradient(135deg, #f8f8f8 0%, #f6f2fa 100%);
  color: rgb(56, 55, 55);
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: width 0.3s ease;
  z-index: 999;
  border: none;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
  justify-content: center;
}

.logo {
  width: 200px;
  height: 40px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.sidebar.collapsed .logo {
  width: 40px;
  height: 40px;
}

.company-name {
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  transition: opacity 0.3s ease;
}

.sidebar.collapsed .company-name {
  display: none;
}

.sidebar-nav {
  padding: 1rem 0;
}

.menu-section {
  margin-bottom: 0.5rem;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: none;
  position: relative;
}

.sidebar.collapsed .menu-item {
  padding: 1rem 0.5rem;
  justify-content: center;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.993);
}

.menu-icon {
  font-size: 1.25rem;
  margin-right: 1rem;
  width: 24px;
  text-align: center;
  transition: margin 0.3s ease;
}

.sidebar.collapsed .menu-icon {
  margin-right: 0;
}

.menu-label {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  flex: 1;
  transition: opacity 0.3s ease;
}

.sidebar.collapsed .menu-label {
  display: none;
}

.menu-arrow {
  transition: transform 0.3s ease;
  font-size: 1.25rem;
}

.sidebar.collapsed .menu-arrow {
  display: none;
}

.menu-arrow.rotated {
  transform: rotate(90deg);
}

/* Submenu normal (sidebar expandido) */
.submenu {
  background: linear-gradient(135deg, #f8f8f8 0%, #f6f2fa 100%);
  margin-left: 1rem;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sidebar.collapsed .submenu {
  display: none;
}

/* Submenu flutuante (sidebar recolhido) */
.floating-submenu {
  position: absolute;
  left: 70px;
  top: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  border: 1px solid #e2e8f0;
  animation: slideIn 0.2s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: none;
}

.submenu-item:hover {
  background: #f7fafc;
}

.submenu-icon {
  font-size: 1rem;
  margin-right: 0.75rem;
  width: 20px;
  text-align: center;
}

.submenu-label {
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  color: #2d3748;
}

/* Tooltip para quando estiver recolhido */
.sidebar.collapsed .menu-item:hover::before {
  content: attr(data-tooltip);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  background: #1a202c;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  white-space: nowrap;
  z-index: 1001;
  margin-left: 0.5rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Remove qualquer borda ou separador adicional */
.sidebar-nav::before,
.menu-section::before,
.menu-item::before {
  display: none;
}
</style>